import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import './i18n'
import './index.css'
import App from './App.tsx'
import { ToastProvider } from './components/overlay/ToastProvider'
import { ConfirmProvider } from './components/overlay/ConfirmProvider'
import logger from './services/logger'
import { shouldReloadOnControllerChange } from './serviceWorkerRegistration'
import { notifyServiceWorkerUpdate } from './serviceWorkerUpdate'
import { AppUpdateToast } from './components/overlay/AppUpdateToast'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
    },
  },
})

import * as Sentry from "@sentry/react";

// Only initialize Sentry in non-test environments
if (import.meta.env.MODE !== 'test') {
  Sentry.init({
    dsn: "https://68136c10737ddf7ce931aa4510a502be@o4510854780485632.ingest.de.sentry.io/4510854852509776",
    environment: import.meta.env.MODE, // 'development' or 'production'
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration()
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", "mp.batouta.ovh"],
    // Session Replay
    replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Enable logs to be sent to Sentry
    enableLogs: true
  });
}

// Hide the initial HTML loader once React is ready
const hideInitialLoader = () => {
  const loader = document.getElementById('initial-loader')
  if (loader) {
    loader.classList.add('hidden')
    // Remove from DOM after fade out
    setTimeout(() => loader.remove(), 300)
  }
}

createRoot(document.getElementById('root')!).render(
  <QueryClientProvider client={queryClient}>
    <ToastProvider>
      <AppUpdateToast />
      <ConfirmProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ConfirmProvider>
    </ToastProvider>
    <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
  </QueryClientProvider>
)

// Hide loader after React has mounted
hideInitialLoader()

// Register service worker for PWA functionality in production only.
// In development, aggressively remove old registrations/caches so stale
// workers cannot interfere with API streaming or HMR.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  const hadControllerOnLoad = Boolean(navigator.serviceWorker.controller)
  // Updates are consent-gated: a takeover reloads the tab, which can kill
  // long-running work mid-flight (on-device model downloads, chat turns,
  // exports). When a new SW is waiting, <AppUpdateToast> asks the user; its
  // Reload action is the ONLY path that posts SKIP_WAITING. If the user
  // declines, the new version lands on their next natural navigation or when
  // the last old-SW tab closes.
  //
  // When a new SW takes over (skipWaiting → clients.claim), `controllerchange`
  // fires in EVERY open tab, not only in the tab that asked for the update.
  // Only this tab reloads if it requested the takeover here, so one tab's
  // accepted update never force-reloads the others; they pick up the new
  // version on their next natural navigation or reload.
  // Skip that reload on the first install or during an active navigation such
  // as the OAuth handoff, otherwise the browser can cancel the outgoing
  // document request and drop the user back on the app.
  let isRefreshing = false;
  let updateRequestedHere = false;
  let isNavigatingAway = false;

  window.addEventListener('beforeunload', () => {
    isNavigatingAway = true;
  });

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!shouldReloadOnControllerChange({ hadControllerOnLoad, isRefreshing, isNavigatingAway, updateRequestedHere })) return;
    isRefreshing = true;
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', {
        // Never serve sw.js from the HTTP cache — always hit the network so
        // updated versions take effect without requiring a hard reload.
        updateViaCache: 'none',
      })
      .then((registration) => {
        logger.debug('Service Worker registered successfully', { scope: registration.scope });

        const offerUpdate = () => {
          notifyServiceWorkerUpdate(() => {
            updateRequestedHere = true;
            // Read `waiting` at consent time: a newer worker may have replaced
            // the one that triggered the announcement.
            registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
            window.location.reload();
          });
        };

        // A new SW may already be waiting when this page loads (installed by
        // another tab, or the user dismissed the prompt before a reload).
        if (registration.waiting) {
          offerUpdate();
        }

        // Or it may finish installing while this page is open.
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && registration.waiting) {
              offerUpdate();
            }
          });
        });

        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60000); // Check every minute
      })
      .catch((error) => {
        logger.debug('Service Worker registration failed', { error });
      });
  });

  // Handle navigation messages from SW notificationclick handler
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NAVIGATE' && event.data.url) {
      window.location.href = event.data.url;
    }
  });
}

if ('serviceWorker' in navigator && import.meta.env.DEV) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.getRegistrations()
      .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
      .catch(() => {
        // Best-effort cleanup only.
      });

    if ('caches' in window) {
      caches.keys()
        .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
        .catch(() => {
          // Best-effort cleanup only.
        });
    }
  });
}
